import { useId } from 'react';
import type { Ownership } from '../../../data/referenceGuides/types';
interface MatrixBlockProps {
  columns: string[];
  rows: Array<{
    capability: string;
    values: Ownership[];
  }>;
  legendNote?: string;
  caption: string;
}

/**
 * Capability ownership grid.
 *
 * This is the highest-value asset in the reference packets and the easiest one
 * to render inaccessibly. Three rules hold it together:
 *
 *  1. Real table semantics — <caption>, <th scope="col">, <th scope="row">.
 *     Without scope, a screen reader cannot associate a dot with its column.
 *  2. Every cell carries a visually-hidden word. A coloured dot alone is a
 *     WCAG 1.1.1 failure; the cell would be announced as empty.
 *  3. The marks differ in SHAPE as well as colour (filled / ring / dash), so
 *     the legend survives greyscale printing and colour-blindness. That is
 *     WCAG 1.4.1 — never encode meaning in hue alone.
 *
 * The scroll container is focusable (tabindex=0 + role=region + a name) because
 * a keyboard-only user has no other way to reach the off-screen columns.
 */
const OWNERSHIP_LABEL: Record<Ownership, string> = {
  2: 'Primary owner',
  1: 'Secondary, contributes',
  0: 'Not applicable'
};
function OwnershipMark({
  value
}: {
  value: Ownership;
}) {
  return <>
      <span className="sr-only" data-id="element-295" data-mp="u21n4tft20on">{OWNERSHIP_LABEL[value]}</span>
      {value === 2 && <span aria-hidden="true" className="inline-block w-2.5 h-2.5 rounded-full bg-violet-link align-middle" data-id="element-296" data-mp="1qrsg2o8g7fsc" />}
      {value === 1 && <span aria-hidden="true" className="inline-block w-2.5 h-2.5 rounded-full border-2 border-violet-link/70 align-middle" data-id="element-297" data-mp="1mpxgppa2vch7" />}
      {value === 0 && <span aria-hidden="true" className="inline-block w-2.5 h-0.5 bg-white/25 align-middle" data-id="element-298" data-mp="1hp9iojnflvdh" />}
    </>;
}
export function MatrixBlock({
  columns,
  rows,
  legendNote,
  caption
}: MatrixBlockProps) {
  const captionId = useId();
  return <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] overflow-hidden" data-id="element-299" data-mp="1d45msjf47wy">
      <div role="region" aria-labelledby={captionId} tabIndex={0} className="overflow-x-auto focus-visible:outline-2 focus-visible:outline-violet-link" data-id="element-300" data-mp="2u00j0kt6rhu">
        <table className="w-full text-left border-collapse min-w-[640px]" data-id="element-301" data-mp="7sksydttk6vq">
          <caption id={captionId} className="sr-only" data-id="element-302" data-mp="3pxo1k8izv10w">
            {caption}
          </caption>
          <thead data-id="element-303" data-mp="3ux0wlgk0reek">
            <tr className="border-b border-white/[0.1] bg-white/[0.03]" data-id="element-304" data-mp="3uwhjqe987g7e">
              <th scope="col" className="py-3.5 px-5 text-white/60 text-xs font-semibold uppercase tracking-widest" data-id="element-305" data-mp="3g8nzuhd3xrj0">
                Capability
              </th>
              {columns.map(col => <th key={col} scope="col" className="py-3.5 px-3 text-white/60 text-xs font-semibold uppercase tracking-widest text-center whitespace-nowrap" data-id="element-306" data-mp="378hrtb06hjr8">
                  {col}
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-307" data-mp="2xbd0kwrh6y3k">
            {rows.map((row, i) => <tr key={row.capability} className={i < rows.length - 1 ? 'border-b border-white/[0.06]' : undefined} data-id="element-308" data-mp="2xcegbejfs8rm" data-mpi={i}>
                <th scope="row" className="py-3 px-5 text-white/70 text-sm font-medium text-left sticky left-0 bg-navy" data-id="element-309" data-mp="2in37p8mnb80w">
                  {row.capability}
                </th>
                {row.values.map((value, j) => <td key={columns[j]} className="py-3 px-3 text-center" data-id="element-310" data-mp="2sm2aiakqc9fz" data-mpi={j}>
                    <OwnershipMark value={value} data-id="element-311" data-mp="2nmfvpwp96l52" />
                  </td>)}
              </tr>)}
          </tbody>
        </table>
      </div>

      <div className="px-5 py-4 border-t border-white/[0.08] flex flex-wrap items-center gap-x-6 gap-y-2" data-id="element-312" data-mp="31lfjl24xyw06">
        <span className="flex items-center gap-2 text-white/55 text-sm" data-id="element-313" data-mp="2wm30msh6y92v">
          <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full bg-violet-link" data-id="element-314" data-mp="3bcjyy2yj4ivj" />
          Primary owner
        </span>
        <span className="flex items-center gap-2 text-white/55 text-sm" data-id="element-315" data-mp="36dz0o92u899q">
          <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full border-2 border-violet-link/70" data-id="element-316" data-mp="3kc70qpv67qp4" />
          Secondary / contributes
        </span>
        <span className="flex items-center gap-2 text-white/55 text-sm" data-id="element-317" data-mp="3gcy7rn7yfwum">
          <span aria-hidden="true" className="w-2.5 h-0.5 bg-white/25" data-id="element-318" data-mp="1dnn5zk0v4hsb" />
          Not applicable
        </span>
        {legendNote && <span className="text-white/40 text-xs ml-auto" data-id="element-319" data-mp="1ilgbeaucd7rj">{legendNote}</span>}
      </div>
    </div>;
}